import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Divider,
    FormControl,
    Select,
    MenuItem,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import EditIcon from '../../../assets/icons/EditBlue.svg';
import UploadedIcon from '../../../assets/icons/Uploaded.svg';
import ReplaceLogoIcon from '../../../assets/icons/ReplaceLogo.svg';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import UploadPracticeLogoModal from '../UploadPracticeLogoModal';
import { practiceOrClinicText } from '../../../config';

const SettingsClinic = ({
    clinicianData,
    handleChangeInput,
    clinicImage,
    handleFileChange,
    setIsDeleteLogoDialogOpen,
    setClinicianData,
    updateClinicData,
    readOnly = false,
    isFreeplan = false,
    onUpgradeClick = () => {},
    isLogoUploadBlocked = false,
    onLogoUpgradeClick = () => {},
    bannerText = '',
    showEditAction = true,
    sectionSx = {},
    layout = 'stacked',
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [editorActive, setEditorActive] = useState(false);
    const [logoModalOpen, setLogoModalOpen] = useState(false);
    const [selectedLogoFile, setSelectedLogoFile] = useState(null);
    const [nameError, setNameError] = useState(false);
    const [clinicValidationError, setClinicValidationError] = useState('');
    const [logoDimensions, setLogoDimensions] = useState('');
    const fieldDescriptionSx = {
        color: '#616063',
        fontFamily: 'Inter',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '14px',
        letterSpacing: '-0.28px',
        mb: '10px',
    };

    // Keep logo metadata derived from the current image source so the panel stays in sync after upload/remove.
    useEffect(() => {
        if (!clinicImage) {
            setLogoDimensions('');
            return;
        }

        const image = new Image();
        image.onload = () => {
            setLogoDimensions(`${image.naturalWidth}px x ${image.naturalHeight}px`);
        };
        image.onerror = () => {
            setLogoDimensions('');
        };
        image.src = clinicImage;
    }, [clinicImage]);

    const clinicLogoFileName = useMemo(() => {
        const clinicImageId = clinicianData?.clinicData?.clinicImageId || '';
        if (!clinicImageId) return '';

        const segments = clinicImageId.split('/');
        const rawFileName = segments[segments.length - 1] || clinicImageId;

        return rawFileName.replace(/^[0-9]+-/, '');
    }, [clinicianData?.clinicData?.clinicImageId]);

    const handleLogoUploadClick = () => {
        if (!editorActive || readOnly) return;
        if (isLogoUploadBlocked) {
            onLogoUpgradeClick();
            return;
        }
        setLogoModalOpen(true);
    };

    const validatePracticeDetails = () => {
        const clinicName = (clinicianData?.clinicData?.clinicName || '').trim();
        if (!clinicName) {
            setNameError(true);
            setClinicValidationError(`${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name is required`);
            return false;
        }
        setNameError(false);
        setClinicValidationError('');
        return true;
    };

    const renderLogoPanel = () => {
        const hasLogo = Boolean(clinicImage);
        const logoAltText = `${practiceOrClinicText} logo`;
        const logoLabel = `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} logo`;
        const panelIsInteractive = editorActive && !readOnly;
        const isDesktopLogoPanel = layout === 'two-column' && !isMobile;
        const isMobileLogoPanel = isMobile && !isDesktopLogoPanel;
        const mobileLogoDividerLeft = '43%';
        const logoMetadataTypography = {
            color: '#616063',
            fontFamily: 'Inter',
            fontSize: isDesktopLogoPanel ? '14px' : '10px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: isDesktopLogoPanel ? '14px' : '14px',
            letterSpacing: isDesktopLogoPanel ? '-0.28px' : 0,
        };

        return (
            <Box>
                {layout === 'two-column' && (
                    <Typography sx={fieldDescriptionSx}>{logoLabel}</Typography>
                )}

                {!hasLogo ? (
                    <Box
                        sx={{
                            border: '1px dashed rgba(0,4,83,0.08)',
                            background: 'rgba(240,240,252,0.5)',
                            borderRadius: layout === 'two-column' ? '12px' : '8px',
                            minHeight: layout === 'two-column' ? 160 : 127,
                            p: layout === 'two-column' ? 2 : '14px 16px 12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            opacity: panelIsInteractive ? 1 : 0.55,
                            filter: panelIsInteractive ? 'none' : 'grayscale(100%)',
                            cursor: panelIsInteractive ? 'pointer' : 'default',
                        }}
                        onClick={handleLogoUploadClick}
                    >
                        <Typography sx={{ fontSize: 14, color: '#616063', mb: 1 }}>
                            {isMobile ? (
                                <>
                                    <Box component="span" sx={{ color: '#FF669B', textDecoration: 'underline' }}>
                                        Choose a file
                                    </Box>{' '}
                                    to upload
                                </>
                            ) : (
                                <>
                                    Drag & Drop or{' '}
                                    <Box component="span" sx={{ color: '#FF669B', textDecoration: 'underline' }}>
                                        Choose a file
                                    </Box>{' '}
                                    to upload
                                </>
                            )}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#616063' }}>
                            Please ensure the image dimensions do not exceed 700x700 pixels
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            border: '1px dashed rgba(0,4,83,0.08)',
                            background: '#fff',
                            borderRadius: isDesktopLogoPanel ? '13.259px' : '8px',
                            width: isDesktopLogoPanel ? '471px' : '100%',
                            maxWidth: '100%',
                            height: isDesktopLogoPanel ? '213px' : isMobileLogoPanel ? '127px' : 'auto',
                            borderWidth: isDesktopLogoPanel ? '2.21px' : '1px',
                            borderColor: isDesktopLogoPanel ? 'rgba(0, 4, 83, 0.05)' : 'rgba(0,4,83,0.08)',
                            borderStyle: 'dashed',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isDesktopLogoPanel || isMobileLogoPanel ? 'row' : 'column',
                                alignItems: isDesktopLogoPanel || isMobileLogoPanel ? 'stretch' : 'flex-start',
                                gap: isDesktopLogoPanel || isMobileLogoPanel ? 0 : 1.5,
                                px: isDesktopLogoPanel || isMobileLogoPanel ? 0 : 1.5,
                                pt: isDesktopLogoPanel || isMobileLogoPanel ? 0 : 1.5,
                                pb: isDesktopLogoPanel || isMobileLogoPanel ? 0 : 1.5,
                                height: isDesktopLogoPanel ? '163px' : isMobileLogoPanel ? '101px' : 'auto',
                                position: 'relative',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                sx={{
                                    width: isDesktopLogoPanel ? '235px' : isMobileLogoPanel ? mobileLogoDividerLeft : '100%',
                                    minHeight: isDesktopLogoPanel ? '113px' : 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: isDesktopLogoPanel || isMobileLogoPanel ? 'center' : 'flex-start',
                                    pb: 0,
                                    flexShrink: 0,
                                    ...(isDesktopLogoPanel
                                        ? {
                                              position: 'absolute',
                                              left: 0,
                                              top: 0,
                                              height: '163px',
                                          }
                                        : {}),
                                    ...(isMobileLogoPanel
                                        ? {
                                              height: '101px',
                                          }
                                        : {}),
                                    boxSizing: 'border-box',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={clinicImage}
                                    alt={logoAltText}
                                    onLoad={(event) => {
                                        const { naturalWidth, naturalHeight } = event.currentTarget;
                                        if (naturalWidth && naturalHeight) {
                                            setLogoDimensions(`${naturalWidth}px x ${naturalHeight}px`);
                                        }
                                    }}
                                    sx={{
                                        maxWidth: isDesktopLogoPanel ? 174 : isMobileLogoPanel ? 112 : 120,
                                        maxHeight: isDesktopLogoPanel ? 88 : isMobileLogoPanel ? 72 : 72,
                                        width: isMobileLogoPanel ? 'auto' : '100%',
                                        objectFit: 'contain',
                                        display: 'block',
                                    }}
                                />
                            </Box>

                            {(isDesktopLogoPanel || isMobileLogoPanel) && (
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        width: isDesktopLogoPanel ? '2.21px' : '1px',
                                        height: isDesktopLogoPanel ? '133px' : '79px',
                                        position: 'absolute',
                                        left: isDesktopLogoPanel ? '235px' : mobileLogoDividerLeft,
                                        top: isDesktopLogoPanel ? '21px' : '11px',
                                        bgcolor: 'rgba(0, 4, 83, 0.08)',
                                        flexShrink: 0,
                                    }}
                                />
                            )}

                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    justifyContent: 'center',
                                    pb: 0,
                                    ...(isDesktopLogoPanel
                                        ? {
                                              position: 'absolute',
                                              left: '260.21px',
                                              right: 0,
                                              top: '21px',
                                              height: '133px',
                                              justifyContent: 'flex-start',
                                          }
                                        : {}),
                                    ...(isMobileLogoPanel
                                        ? {
                                              position: 'absolute',
                                              left: `calc(${mobileLogoDividerLeft} + 10px)`,
                                              right: '4px',
                                              top: '10px',
                                              height: '82px',
                                              justifyContent: 'flex-start',
                                          }
                                        : {}),
                                    boxSizing: 'border-box',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: isDesktopLogoPanel ? '4px' : 0.5,
                                        bgcolor: 'rgba(0, 216, 0, 0.25)',
                                        color: '#008900',
                                        borderRadius: '60px',
                                        width: isDesktopLogoPanel ? '104px' : isMobileLogoPanel ? '72px' : 'auto',
                                        height: isDesktopLogoPanel ? '29px' : isMobileLogoPanel ? '17px' : 'auto',
                                        justifyContent: 'center',
                                        px: isDesktopLogoPanel || isMobileLogoPanel ? 0 : 1,
                                        py: isDesktopLogoPanel || isMobileLogoPanel ? 0 : '3px',
                                        mb: isDesktopLogoPanel ? '14px' : isMobileLogoPanel ? '8px' : 1,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={UploadedIcon}
                                        alt=""
                                        sx={{
                                            width: isDesktopLogoPanel ? 14 : 10,
                                            height: isDesktopLogoPanel ? 14 : 10,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: isDesktopLogoPanel ? '14px' : '10px',
                                            fontWeight: 400,
                                            lineHeight: isDesktopLogoPanel ? '14px' : '10px',
                                            letterSpacing: isDesktopLogoPanel ? '-0.28px' : 0,
                                            color: '#008900',
                                        }}
                                    >
                                        Uploaded
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        ...logoMetadataTypography,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {clinicLogoFileName || 'Practice logo'}
                                </Typography>
                                <Typography sx={{ ...logoMetadataTypography, mt: isDesktopLogoPanel ? '14px' : 0, mb: isDesktopLogoPanel ? '13px' : 1.25 }}>
                                    {logoDimensions || 'Resolution unavailable'}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: isDesktopLogoPanel ? '8px' : isMobileLogoPanel ? '4px' : 1,
                                        flexWrap: 'nowrap',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={handleLogoUploadClick}
                                        disabled={!panelIsInteractive}
                                        sx={{
                                            border: 'none',
                                            background: '#FF669B',
                                            color: '#fff',
                                            borderRadius: '72px',
                                            width: isDesktopLogoPanel ? '108.81467px' : isMobileLogoPanel ? '72px' : 'auto',
                                            height: isDesktopLogoPanel ? '30px' : isMobileLogoPanel ? '18px' : '18px',
                                            px: isDesktopLogoPanel || isMobileLogoPanel ? 0 : 1,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.5,
                                            cursor: panelIsInteractive ? 'pointer' : 'default',
                                            opacity: panelIsInteractive ? 1 : 0.55,
                                            boxShadow: '0px 0px 3.63px rgba(35, 7, 88, 0.10)',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={ReplaceLogoIcon}
                                            alt=""
                                            sx={{
                                                width: isDesktopLogoPanel ? '13.388px' : isMobileLogoPanel ? 7 : 8,
                                                height: isDesktopLogoPanel ? '13.388px' : isMobileLogoPanel ? 7 : 8,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                color: '#FFF',
                                                textShadow: isDesktopLogoPanel
                                                    ? '0 0 6.085px rgba(35, 7, 88, 0.10)'
                                                    : 'none',
                                                fontFamily: 'Inter',
                                                fontSize: isDesktopLogoPanel ? '10.954px' : isMobileLogoPanel ? '7px' : '8px',
                                                fontStyle: 'normal',
                                                fontWeight: 500,
                                                lineHeight: isDesktopLogoPanel ? '10.954px' : isMobileLogoPanel ? '7px' : '8px',
                                                letterSpacing: isDesktopLogoPanel ? '-0.219px' : 0,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Replace logo
                                        </Typography>
                                    </Box>

                                    <Typography
                                        component="button"
                                        type="button"
                                        onClick={() => panelIsInteractive && setIsDeleteLogoDialogOpen(true)}
                                        sx={{
                                            border: 'none',
                                            background: 'transparent',
                                            p: 0,
                                            m: 0,
                                            color: '#FF669B',
                                            fontFamily: 'Inter',
                                            fontSize: isDesktopLogoPanel ? '10px' : '8px',
                                            fontStyle: 'normal',
                                            fontWeight: 500,
                                            lineHeight: isDesktopLogoPanel ? '10px' : '8px',
                                            letterSpacing: isDesktopLogoPanel ? '-0.2px' : 0,
                                            textDecorationLine: 'underline',
                                            textDecorationStyle: 'solid',
                                            textDecorationSkipInk: 'auto',
                                            textDecorationThickness: 'auto',
                                            textUnderlineOffset: 'auto',
                                            textUnderlinePosition: 'from-font',
                                            cursor: panelIsInteractive ? 'pointer' : 'default',
                                            opacity: panelIsInteractive ? 1 : 0.55,
                                        }}
                                    >
                                        Remove logo
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                minHeight: isDesktopLogoPanel ? '50px' : 24,
                                px: isDesktopLogoPanel ? '19px' : isMobileLogoPanel ? '8px' : 1.25,
                                py: isDesktopLogoPanel || isMobileLogoPanel ? 0 : 1,
                                bgcolor: 'rgba(240,240,252,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: isDesktopLogoPanel ? '6px' : 0.75,
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                aria-hidden="true"
                                sx={{
                                    width: isDesktopLogoPanel ? 14 : 8,
                                    height: isDesktopLogoPanel ? 14 : 8,
                                    border: '1px solid #FF669B',
                                    borderRadius: '50%',
                                    color: '#FF669B',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    fontFamily: 'Inter',
                                    fontSize: isDesktopLogoPanel ? '10px' : '6px',
                                    fontWeight: 400,
                                    lineHeight: 1,
                                }}
                            >
                                i
                            </Box>
                            <Typography
                                sx={{
                                    ...logoMetadataTypography,
                                    ...(isMobileLogoPanel
                                        ? {
                                              fontSize: '8px',
                                              lineHeight: '10px',
                                              letterSpacing: '-0.16px',
                                          }
                                        : {}),
                                }}
                            >
                                Your logo will appear at the bottom of all handouts
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    const renderTextField = (label, placeholder, name, value, onChange, isLast = false, extraProps = {}) => (
        <Box key={name} sx={{ marginBottom: isLast ? 0 : 2, position: 'relative' }}>
            {layout === 'two-column' && (
                <Typography sx={fieldDescriptionSx}>{label}</Typography>
            )}
            <TextField
                name={name}
                variant="outlined"
                placeholder={placeholder}
                value={value}
                onChange={editorActive && !readOnly ? onChange : null}
                size="small"
                fullWidth
                error={extraProps.error}
                helperText={extraProps.helperText}
                InputProps={{
                    readOnly: !editorActive || readOnly,
                    style: {
                        border: 'none',
                        background: editorActive && !readOnly ? '#F7F7F7' : 'transparent',
                        marginBottom: layout === 'two-column' ? 0 : 16,
                    },
                    disableUnderline: true,
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        height: layout === 'two-column' ? '42.923px' : undefined,
                        borderRadius: layout === 'two-column' ? '9.198px' : undefined,
                        '& fieldset': {
                            border: layout === 'two-column' ? '0.766px solid rgba(102, 102, 102, 0.35)' : 'none',
                        },
                    },
                    input: {
                        height: layout === 'two-column' ? '42.923px' : 56,
                        color: 'rgb(33, 33, 33)',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '8px',
                        flex: 'none',
                        order: 0,
                        alignSelf: 'stretch',
                        flexGrow: 0,
                        p: 0,
                        pl: layout === 'two-column' ? '9px' : editorActive && !readOnly ? 2 : 0,
                    },
                }}
            />
            {layout !== 'two-column' && !isLast && (
                <Divider
                    sx={{
                        width: 'calc(100% + 48px)',
                        position: 'absolute',
                        bottom: 0,
                        left: '-24px',
                        backgroundColor: 'rgba(229, 229, 229, 1)',
                        display: editorActive ? 'none' : 'block',
                    }}
                />
            )}
        </Box>
    );

    const renderSelectField = (label, placeholder, name, value, onChange, options, isLast = false) => (
        <Box key={name} sx={{ marginBottom: isLast ? 0 : 2, position: 'relative' }}>
            {layout === 'two-column' && (
                <Typography sx={fieldDescriptionSx}>{label}</Typography>
            )}
            {editorActive && !readOnly ? (
                <FormControl fullWidth>
                    <Select
                        name={name}
                        value={value}
                        onChange={onChange}
                        displayEmpty
                        inputProps={{ 'aria-label': placeholder }}
                        sx={{
                            borderRadius: '9.198px',
                            background: '#F7F7F7',
                            height: layout === 'two-column' ? '42.923px' : 56,
                            pl: editorActive ? 2 : 0,
                            '& .MuiOutlinedInput-input': {
                                padding: 0,
                                paddingLeft: layout === 'two-column' ? '9px' : 0,
                                height: layout === 'two-column' ? '42.923px' : undefined,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: layout === 'two-column' ? '0.766px solid rgba(102, 102, 102, 0.35)' : undefined,
                            },
                            mb: 2,
                        }}
                        InputProps={{
                            readOnly: !editorActive,
                            disableUnderline: true,
                        }}
                    >
                        <MenuItem disabled value="">
                            {placeholder}
                        </MenuItem>
                        {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : (
                <TextField
                    name={name}
                    variant="outlined"
                    value={value}
                    placeholder={placeholder}
                    fullWidth
                    InputProps={{
                        readOnly: true,
                        style: {
                            border: 'none',
                            background: 'transparent',
                            marginBottom: layout === 'two-column' ? 0 : 16,
                        },
                        disableUnderline: true,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            height: layout === 'two-column' ? '42.923px' : undefined,
                            borderRadius: layout === 'two-column' ? '9.198px' : undefined,
                            '& fieldset': {
                                border: layout === 'two-column' ? '0.766px solid rgba(102, 102, 102, 0.35)' : 'none',
                            },
                        },
                        input: {
                            height: layout === 'two-column' ? '42.923px' : 56,
                            color: 'rgb(33, 33, 33)',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '8px',
                            flex: 'none',
                            order: 0,
                            alignSelf: 'stretch',
                            flexGrow: 0,
                            p: 0,
                            pl: layout === 'two-column' ? '9px' : editorActive ? 2 : 0,
                        },
                    }}
                />
            )}
            {layout !== 'two-column' && !isLast && (
                <Divider
                    sx={{
                        width: 'calc(100% + 48px)',
                        position: 'absolute',
                        bottom: 0,
                        left: '-24px',
                        backgroundColor: 'rgba(229, 229, 229, 1)',
                        display: editorActive ? 'none' : 'block',
                    }}
                />
            )}
        </Box>
    );

    return (
        <Box
            id="practice-details-section"
            className="settings-block"
            component={'section'}
            sx={{
                backgroundColor: 'white',
                border: '1px solid rgba(102, 102, 102, 0.15)',
                borderRadius: '30px',
                padding: { xs: '20px 22px', md: '24px 30px' },
                my: 3,
                ...sectionSx,
            }}
        >
            {practiceOrClinicText && (
                <>
                    {!!bannerText && (
                        <Typography
                            sx={{
                                background: '#000453',
                                color: 'white',
                                fontWeight: 600,
                                textAlign: 'center',
                                marginX: '-24px',
                                mt: '-16px',
                                mb: 2,
                                padding: 2,
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                            }}
                        >
                            {bannerText}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            position: 'relative',
                            mb: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 0,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 600, height: 48, flex: 1, minWidth: 0 }}>
                            {practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} details
                        </Typography>
                        {!readOnly && showEditAction && (
                            <IconButton
                                onClick={() => {
                                    if (isFreeplan) {
                                        onUpgradeClick();
                                    } else {
                                        setEditorActive(!editorActive);
                                    }
                                }}
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                                sx={{
                                    p: 0,
                                    width: 38,
                                    height: 38,
                                    borderRadius: '38px',
                                    bgcolor: editorActive ? '#000453' : '#FF669B',
                                    '&:hover': { bgcolor: editorActive ? '#1C2A8A' : '#FF85B0' },
                                    '&:active': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                                    '&:focus': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                                    '&.Mui-focusVisible': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                                    WebkitTapHighlightColor: 'transparent',
                                    flexShrink: 0,
                                }}
                            >
                                <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
                            </IconButton>
                        )}
                    </Box>

                    {!clinicianData.clinicData.clinicName && !editorActive && !isFreeplan && (
                        <Typography variant="body2" sx={{ color: 'red', mb: 1 }}>
                            ⚠️ {practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name is required
                        </Typography>
                    )}

                    {layout === 'two-column' ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            <Box>
                                {renderTextField(
                                    `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name`,
                                    `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name`,
                                    'clinicData.clinicName',
                                    clinicianData.clinicData.clinicName,
                                    handleChangeInput,
                                    false,
                                    {
                                        error: nameError && !isFreeplan,
                                        helperText:
                                            nameError && !isFreeplan
                                                ? `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name is required`
                                                : '',
                                    },
                                )}
                                {renderTextField(
                                    `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} phone number`,
                                    `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} phone number`,
                                    'clinicData.clinicNumber',
                                    clinicianData.clinicData.clinicNumber,
                                    (e) => {
                                        const newValue = e.target.value.replace(/[^0-9]/g, '');
                                        handleChangeInput({ target: { name: 'clinicData.clinicNumber', value: newValue } });
                                    },
                                )}
                                {renderSelectField(
                                    `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} size`,
                                    `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} size`,
                                    'clinicData.clinicSize',
                                    clinicianData.clinicData.clinicSize || '',
                                    handleChangeInput,
                                    [
                                        { value: '0-5', label: '0-5 staff' },
                                        { value: '6-10', label: '6-10 staff' },
                                        { value: '11-20', label: '11-20 staff' },
                                        { value: '20+', label: '20+ staff' },
                                    ],
                                    true,
                                )}
                            </Box>
                            <Box>
                                {renderLogoPanel()}
                            </Box>
                        </Box>
                    ) : (
                        <>
                    {renderTextField(
                        `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name`,
                        `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name`,
                        'clinicData.clinicName',
                        clinicianData.clinicData.clinicName,
                        handleChangeInput,
                        false,
                        {
                            error: nameError && !isFreeplan,
                            helperText: nameError && !isFreeplan
                                ? `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} name is required`
                                : '',
                        },
                    )}
                    {renderTextField(
                        `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} phone number`,
                        `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} phone number`,
                        'clinicData.clinicNumber',
                        clinicianData.clinicData.clinicNumber,
                        (e) => {
                            const newValue = e.target.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
                            handleChangeInput({ target: { name: 'clinicData.clinicNumber', value: newValue } });
                        },
                    )}

                    {renderSelectField(
                        `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} size`,
                        `${practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1)} size`,
                        'clinicData.clinicSize',
                        clinicianData.clinicData.clinicSize || '',
                        handleChangeInput,
                        [
                            { value: '0-5', label: '0-5 staff' },
                            { value: '6-10', label: '6-10 staff' },
                            { value: '11-20', label: '11-20 staff' },
                            { value: '20+', label: '20+ staff' },
                        ],
                        true,
                    )}

                    <Box>
                        {renderLogoPanel()}
                        {editorActive && !readOnly && (
                            <PinkGradientButton
                                className="black-btn"
                                onClick={() => {
                                    if (!validatePracticeDetails()) return;
                                    updateClinicData();
                                }}
                                sx={{
                                    mt: 1,
                                    mx: { xs: 'auto', md: 0 },
                                    display: 'block',
                                    height: '44px',
                                    borderRadius: '200px',
                                    background: '#FF669B',
                                    backgroundImage: 'none',
                                    '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                                    color: '#FFF',
                                }}
                            >
                                Save changes
                            </PinkGradientButton>
                        )}
                    </Box>
                    </>
                    )}
                    {editorActive && !readOnly && layout === 'two-column' && (
                        <PinkGradientButton
                            className="black-btn"
                            onClick={() => {
                                if (!validatePracticeDetails()) return;
                                updateClinicData();
                            }}
                            sx={{
                                mt: 1,
                                mx: { xs: 'auto', md: 0 },
                                display: 'block',
                                height: '44px',
                                borderRadius: '200px',
                                background: '#FF669B',
                                backgroundImage: 'none',
                                '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                                color: '#FFF',
                            }}
                        >
                            Save changes
                        </PinkGradientButton>
                    )}
                    {!!clinicValidationError && editorActive && !readOnly && (
                        <Typography sx={{ color: '#d32f2f', mt: 1, fontSize: 12 }}>{clinicValidationError}</Typography>
                    )}
                    <UploadPracticeLogoModal
                        open={logoModalOpen}
                        onClose={() => {
                            setLogoModalOpen(false);
                            setSelectedLogoFile(null);
                        }}
                        selectedFile={selectedLogoFile}
                        onSelectFile={(file) => setSelectedLogoFile(file)}
                        onSave={() => {
                            if (!selectedLogoFile) return;
                            handleFileChange({ target: { files: [selectedLogoFile] } });
                            setLogoModalOpen(false);
                            setSelectedLogoFile(null);
                        }}
                        title={`Upload ${practiceOrClinicText} logo`}
                    />
                </>
            )}
        </Box>
    );
};

export default SettingsClinic;
